import { round, getOption, setInputFilter } from '../../../helper-functions'

/**
 * Manages calculator functionality.
 */
export default function manageCalculator () {
  const calculators = document.querySelectorAll( '.js-calc' )

  calculators.forEach( calc => {
    const inputs = Array.from( calc.querySelector( '.js-calc-inner' ).getElementsByTagName( 'input' ) )
    const reset = calc.querySelector( '.js-calc-reset' )
    const output = calc.querySelector( '.js-calc-result' )
    const outputWrapper = calc.querySelector( '.js-calc-result-wrapper' )
    const loader = calc.querySelector( '.js-calc-loader' )
    const openSettings = calc.querySelector( '.js-open-settings' )
    const closeSettings = calc.querySelector( '.js-close-settings' )
    const calcSettings = calc.querySelector( '.js-calc-settings' )
    const calcSettingsWrapper = calc.querySelector( '.js-calc-settings-wrapper' )
    const wasteOptions = calc.querySelectorAll( 'input[name="waste-option"]' )
    const unitOptions = calc.querySelectorAll( 'input[name="unit-option"]' )
    const unitSm = calc.querySelectorAll( '.js-sm-unit' )
    const unitMd = calc.querySelectorAll( '.js-md-unit' )
    const unitLg = calc.querySelector( '.js-lg-unit' )
    let unit = getOption( unitOptions )

    // Set input filter and add keyup event listener
    inputs.forEach( input => {

      setInputFilter( input, function inputFilter ( value ) {
        return /^\d*\.?\d*$/.test( value ) // Allow digits and '.' only
      })

      input.addEventListener( 'keyup', () => {
        calculate( calc, getOption( unitOptions ), getOption( wasteOptions ), output, outputWrapper, loader, inputs )
      })
    })

    // Reset calculator.
    reset.onclick = () => {
      inputs.forEach( input => {
        input.value = ''
      })

      output.innerText = '00'
      outputWrapper.classList.remove( 'hidden' )
      loader.classList.add( 'hidden' )
    }

    // Open settings add opacity to calculator.
    openSettings.onclick = () => {

      calcSettingsWrapper.classList.remove( 'hidden' )

      setTimeout( () => {
        calcSettingsWrapper.classList.remove( 'bg-transparent' )
        calcSettingsWrapper.classList.add( 'bg-black-overlay' )
        calcSettings.classList.add( 'top-0' )
        calcSettings.classList.remove( '-top-100%' )
      }, 100 )

    }

    // Close settings and update calculator.
    closeSettings.onclick = () => {
      const newUnit = getOption( unitOptions )

      // If unit of measurement has changed, update it on the calculator.
      if ( newUnit !== unit ) {
        unit = newUnit
        updateUnits( unit, unitSm, unitMd, unitLg )
      }

      calculate( calc, getOption( unitOptions ), getOption( wasteOptions ), output, outputWrapper, loader, inputs )

      calcSettingsWrapper.classList.add( 'bg-transparent' )
      calcSettingsWrapper.classList.remove( 'bg-black-overlay' )
      calcSettings.classList.remove( 'top-0' )
      calcSettings.classList.add( '-top-100%' )

      setTimeout( () => {
        calcSettingsWrapper.classList.add( 'hidden' )
      }, 200 )
    }

  })

  /**
   * Manages calculator result.
   *
   * @param {object} calc - The calculator.
   * @param {string} unit - The unit input value.
   * @param {string} waste - The waste input value.
   * @param {object} output - The output node.
   * @param {object} outputWrapper - The output wrapper node.
   * @param {object} loader - The loader node.
   * @param {array} inputs - The calculator inputs.
   */
  function calculate ( calc, unit, waste, output, outputWrapper, loader, inputs ) {

    const hasValue = input => input.value

    // If all fields have a value
    if ( inputs.every( hasValue ) ) {
      const total = getTotal( calc, unit, waste )

      loader.classList.add( 'hidden' )
      outputWrapper.classList.remove( 'hidden' )
      output.innerText = total

      // If one or more fields has a value
    } else if ( inputs.some( hasValue ) ) {

      outputWrapper.classList.add( 'hidden' )
      loader.classList.remove( 'hidden' )

      // If no field has a value
    } else {
      outputWrapper.classList.remove( 'hidden' )
      output.innerText = '00'
      loader.classList.add( 'hidden' )
    }
  }

  /**
   * Updates DOM based on measurement system.
   *
   * @param {string} unit - The measurement system selected.
   * @param {string} sm - The small unit node (cm/in).
   * @param {string} md - The medium unit nodes (m/ft).
   * @param {string} lg - The large unit node (m/yd).
   */
  function updateUnits ( unit, sm, md, lg ) {
    lg.innerText = unit.startsWith( 'unit-us' ) ? 'yd' : 'm'

    // Loop through small and medium units
    sm.forEach( e => {
      e.innerText = unit.startsWith( 'unit-us' ) ? 'in' : 'cm'
    })

    md.forEach( e => {
      e.innerText = unit.startsWith( 'unit-us' ) ? 'ft' : 'm'
    })
  }

  /**
   * Performs calculation depending on calculator type.
   *
   * @param {object} calc - The calculator.
   * @param {string} unit - The unit input value.
   * @param {string} waste - The waste input value.
   * @returns {string} The calculated total.
   */
  function getTotal ( calc, unit, waste ) {
    const type = calc.getAttribute( 'data-calc-type' )
    const hasWaste = waste.startsWith( 'waste-3pct' )
    let total = 0

    if ( 'slab_pour' === type ) {

      const length = calc.querySelector( '.js-calc-length' ).value
      const width = calc.querySelector( '.js-calc-width' ).value
      const depth = calc.querySelector( '.js-calc-depth' ).value

      // Metric formula: length (m) * width (m) * ( depth (cm) / 100 )
      // US formula: length (ft) * width (ft) * ( depth (in) / 12 ) / 27
      total = unit.startsWith( 'unit-metric' ) ? length * width * ( depth / 100 ) : length * width * ( depth / 12 ) / 27

    } else if ( 'circ_vol' === type ) {

      const radius = calc.querySelector( '.js-calc-diameter' ).value / 2
      const area = Math.pow( radius, 2 ) * Math.PI
      const depth = calc.querySelector( '.js-calc-depth' ).value

      // Metric formula: area (m) * ( depth (cm) / 100 )
      // US formula: area (ft) * ( depth (in) / 12 ) / 27
      total = unit.startsWith( 'unit-metric' ) ? area * ( depth / 100 ) : area * ( depth / 12 ) / 27
    } else {

      const length = calc.querySelector( '.js-calc-length' ).value
      const width = calc.querySelector( '.js-calc-width' ).value
      const depth = calc.querySelector( '.js-calc-depth' ).value

      // Metric formula: length (m) * ( width (cm) / 100 ) * ( depth (cm) / 100 )
      // US formula: length (ft) * ( width (in) / 12 ) * ( depth (in) / 12 ) / 27
      total = unit.startsWith( 'unit-metric' )
        ? length * ( width / 100 ) * ( depth / 100 )
        : length * ( width / 12 ) * ( depth / 12 ) / 27
    }

    total += hasWaste ? total * .03 : 0
    const rounded = round( total, 0 ).toString()
    if ( rounded.length <= 8 ) {
      return rounded
    } else {
      return 'Error'
    }
  }
}
